import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { NodeSettingsProps } from '../../types';
import { useFlowDisplayLanguage } from '../../displayLanguage';
import { useNodeFieldState } from '../../hooks';
import { normalizeNumericInput } from '../../normalizeNumericInput';

const DEFAULT_SECONDS = 1;
const MIN_SECONDS = 0;
const MAX_SECONDS = 60;

const normalizeSeconds = (raw: string): number => {
  if (raw.trim() === '') return DEFAULT_SECONDS;
  const parsed = Number(normalizeNumericInput(raw));
  if (!Number.isFinite(parsed)) return DEFAULT_SECONDS;
  const clamped = Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, parsed));
  return Math.round(clamped * 10) / 10;
};

export function WaitSettings({ node }: NodeSettingsProps<'wait'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const commitSeconds = useCallback((raw: string) => {
    const seconds = normalizeSeconds(raw);
    updateNodeData(node.id, { seconds });
    return String(seconds);
  }, [node.id, updateNodeData]);
  const secondsField = useNodeFieldState(
    String(Number.isFinite(node.data.seconds) ? node.data.seconds : DEFAULT_SECONDS),
    commitSeconds,
  );

  return (
    <label className="block" data-product-tour="card-input-1">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {displayLanguage === 'en' ? 'seconds' : '秒数'}
      </span>
      <input
        type="text"
        inputMode="decimal"
        min={MIN_SECONDS}
        max={MAX_SECONDS}
        step="0.1"
        value={secondsField.value}
        onChange={(event) => secondsField.setValue(event.target.value)}
        onBlur={secondsField.commit}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
    </label>
  );
}
